export interface ModelConfig {
  id: string
  name: string
  provider: 'hf-inference' | 'hf-space' | 'replicate'
  hf_model_id: string
  type: 'image' | 'video'
  nsfw: boolean
  default_steps: number
  default_width: number
  default_height: number
  speed: string
  description: string
  // Optional retail price hint for the UI ("free", "~$0.025", etc.)
  cost_estimate?: string
}

// IMPORTANT: Model IDs here MUST match worker/routes/models.ts
// Worker is the source of truth — it gates generation via isAllowedModel()
export const MODELS: ModelConfig[] = [
  {
    id: 'flux-schnell',
    name: 'FLUX.1 Schnell',
    provider: 'hf-inference',
    hf_model_id: 'black-forest-labs/FLUX.1-schnell',
    type: 'image',
    nsfw: false,
    default_steps: 4,
    default_width: 1024,
    default_height: 1024,
    speed: '~2s',
    description: 'Fast, free, 4-step diffusion. Great for drafts.',
    cost_estimate: 'free',
  },
  // ❌ FLUX.1 Dev removed — non-commercial license. See same note in
  // worker/routes/models.ts. Use flux-schnell (Apache) or flux-1.1-pro
  // (commercial paid API) as the quality ladder instead.
  {
    id: 'flux-1-1-pro',
    name: 'FLUX 1.1 Pro',
    provider: 'replicate',
    hf_model_id: 'black-forest-labs/flux-1.1-pro',
    type: 'image',
    nsfw: false,
    default_steps: 25,
    default_width: 1024,
    default_height: 1024,
    speed: '~15s',
    description: 'Top-tier photoreal model. Premium results.',
    cost_estimate: '~$0.04',
  },
  {
    id: 'wan2-2-video',
    name: 'Wan2.2 14B Video',
    provider: 'hf-space',
    hf_model_id: 'Wan-AI/Wan2.2-T2V-14B',
    type: 'video',
    nsfw: false,
    default_steps: 20,
    default_width: 1280,
    default_height: 720,
    speed: '~60s',
    description: 'Image-to-video, 3-5s clips',
  },
  {
    id: 'ltx-2-video',
    name: 'LTX-2 Turbo Video',
    provider: 'hf-space',
    hf_model_id: 'Lightricks/LTX-Video-2-Turbo',
    type: 'video',
    nsfw: false,
    default_steps: 8,
    default_width: 1280,
    default_height: 720,
    speed: '~30s',
    description: 'Fast text/image-to-video',
  },
]

export const IMAGE_MODELS = MODELS.filter((m) => m.type === 'image')
export const VIDEO_MODELS = MODELS.filter((m) => m.type === 'video')

export function getModelById(id: string): ModelConfig | undefined {
  const found = MODELS.find((m) => m.id === id)
  if (found) return found
  // Dynamic LoRA pseudo-models don't live in the static registry. Return a
  // safe synthetic config so callers like Studio can still build a generate
  // request — the worker owns the real dataset lookup.
  if (id.startsWith('lora:')) {
    return {
      id,
      name: 'Custom LoRA',
      provider: 'replicate',
      hf_model_id: '',
      type: 'image',
      nsfw: false,
      default_steps: 28,
      default_width: 1024,
      default_height: 1024,
      speed: '~10s',
      description: 'Custom-trained LoRA',
      cost_estimate: '~$0.025',
    }
  }
  return undefined
}

export function getModelsByAccess(canNSFW: boolean): ModelConfig[] {
  return canNSFW ? MODELS : MODELS.filter((m) => !m.nsfw)
}
